import type { AuditEntry } from './types'

function sha256(ascii: string): string {
  function rightRotate(value: number, amount: number): number {
    return (value >>> amount) | (value << (32 - amount))
  }

  const maxWord = Math.pow(2, 32)
  let result = ''
  const words: number[] = []
  const asciiBitLength = ascii.length * 8

  let hash: [number, number, number, number, number, number, number, number] = [
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
    0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19
  ]

  const k: number[] = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
  ]

  let compositeClear = ascii + '\x80'
  while ((compositeClear.length % 64) !== 56) compositeClear += '\x00'
  for (let i = 0; i < compositeClear.length; i++) {
    const j = compositeClear.charCodeAt(i)
    if (j >> 8) return ''
    words[i >> 2] = (words[i >> 2] || 0) | (j << ((3 - (i % 4)) * 8))
  }
  words.push((asciiBitLength / maxWord) | 0)
  words.push(asciiBitLength | 0)

  for (let j = 0; j < words.length;) {
    const w = words.slice(j, (j += 16))
    const oldHash = [...hash]

    for (let i = 0; i < 64; i++) {
      const w15 = w[i - 15] ?? 0
      const w2 = w[i - 2] ?? 0
      const a = hash[0]
      const e = hash[4]
      const temp1 =
        hash[7] +
        (rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25)) +
        ((e & hash[5]) ^ (~e & hash[6])) +
        (k[i] ?? 0) +
        (w[i] =
          i < 16
            ? (w[i] ?? 0)
            : ((w[i - 16] ?? 0) +
                (rightRotate(w15, 7) ^ rightRotate(w15, 18) ^ (w15 >>> 3)) +
                (w[i - 7] ?? 0) +
                (rightRotate(w2, 17) ^ rightRotate(w2, 19) ^ (w2 >>> 10))) |
              0)
      const temp2 =
        (rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22)) +
        ((a & hash[1]) ^ (a & hash[2]) ^ (hash[1] & hash[2]))

      hash = [
        (temp1 + temp2) | 0,
        a,
        hash[1],
        hash[2],
        (hash[3] + temp1) | 0,
        hash[4],
        hash[5],
        hash[6]
      ]
    }

    for (let i = 0; i < 8; i++) {
      hash[i] = ((hash[i] ?? 0) + (oldHash[i] ?? 0)) | 0
    }
  }

  for (let i = 0; i < 8; i++) {
    for (let j = 3; j >= 0; j--) {
      const b = ((hash[i] ?? 0) >> (j * 8)) & 255
      result += (b < 16 ? '0' : '') + b.toString(16)
    }
  }
  return result
}

function hmacSha256(key: string, message: string): string {
  return sha256(`${key}:${message}`)
}

export class AuditChainService {
  /**
   * Computes SHA-256 over prevHash + timestamp + tenantId + userId + entityType + entityId + action + JSON.stringify(changes)
   */
  static computeEntryHash(entry: Omit<AuditEntry, 'hash'>, secretKey?: string): string {
    // Collect components as per requirements
    const prevHash = entry.prevHash || ''
    const timestamp = entry.performedAt
    const tenantId = entry.tenantId
    const userId = entry.performedBy
    const entityType = entry.entityType
    const entityId = entry.entityId
    const action = entry.action

    // Stringify changes
    const changesObj = {
      previousValues: entry.previousValues,
      newValues: entry.newValues,
      metadata: entry.metadata
    }
    const changes = JSON.stringify(changesObj)

    const payload = `${prevHash}${timestamp}${tenantId}${userId}${entityType}${entityId}${action}${changes}`
    
    if (secretKey) {
      return hmacSha256(secretKey, payload)
    } else {
      return sha256(payload)
    }
  }

  /**
   * Appends a new entry to the log, linking it to the previous record's hash
   */
  static appendAuditEntry(log: AuditEntry[], newRecordData: Omit<AuditEntry, 'hash' | 'prevHash'>): AuditEntry {
    const prevHash = log.length > 0 ? log[log.length - 1]!.hash : undefined
    const entryWithoutHash = {
      ...newRecordData,
      prevHash,
    }
    const hash = this.computeEntryHash(entryWithoutHash)
    const newEntry: AuditEntry = {
      ...entryWithoutHash,
      hash,
    }
    log.push(newEntry)
    return newEntry
  }

  /**
   * Detects any mutation, deletion, or reordering of audit records
   */
  static verifyChainIntegrity(chain: AuditEntry[]): { isValid: boolean; corruptedIndex?: number; reason?: string } {
    let expectedPrevHash: string | undefined = undefined

    for (let i = 0; i < chain.length; i++) {
      const entry = chain[i]
      if (!entry) continue

      // Check link
      if (entry.prevHash !== expectedPrevHash) {
        return { isValid: false, corruptedIndex: i, reason: 'Broken link: prevHash does not match previous hash' }
      }

      // Check current hash
      const computedHash = this.computeEntryHash(entry)
      if (entry.hash !== computedHash) {
        return { isValid: false, corruptedIndex: i, reason: 'Data mutated: hash mismatch' }
      }

      expectedPrevHash = entry.hash
    }

    return { isValid: true }
  }
}
